const React = require("react");
const { MdRefresh } = require("react-icons/md");
const { countryName, selectedOption } = require("../../cjShippingDestinations");
const h = React.createElement;

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const LABEL_TEXT = {
  cheapest: ["h_cjms_label_cheapest", "Cheapest"],
  fastest: ["h_cjms_label_fastest", "Fastest"],
  unavailable: ["h_cjms_label_unavailable", "Not available for every selected variant"],
};

// Status of ONE country ("waiting" / "loading" / "success" / "error"),
// independent from every other country.
const CountryStatus = ({ state, t }) => {
  if (!state || state.status === "idle") return h("span", { className: "cjms-status" }, t("h_cjms_status_idle", "Not calculated"));
  if (state.status === "waiting") return h("span", { className: "cjms-status" }, t("h_cjms_status_waiting", "Waiting..."));
  if (state.status === "loading") return h("span", { className: "cjms-status cjms-status--loading", role: "status" }, h("span", { className: "cjds-sim-loading__spinner cjms-spinner" }), t("h_cjms_status_loading", "Calculating..."));
  if (state.status === "error") return h("span", { className: "cjms-status cjms-status--error" }, t("h_cjms_status_error", "Error"));
  if (state.stale) return h("span", { className: "cjms-status cjms-status--stale" }, t("h_cjms_status_stale", "Out of date -- refresh"));
  return h("span", { className: "cjms-status cjms-status--done" }, t("h_cjms_status_done", "Done"));
};

// One destination country: its own shipping methods, selected method, cost,
// delivery time, optional selling price, profit and sellActive switch.
// Changing anything here never touches another country.
const CJShippingCountryCard = ({
  code, state, computed, priceMode, desiredProfit, defaultSellingPrice,
  onSelectCarrier, onOverrideChange, onToggleActive, onRefresh, t, busy = false,
}) => {
  const option = selectedOption(state);
  const options = state?.options || [];
  const pricing = computed?.pricing || null;
  const profit = pricing ? pricing.profit : null;
  const active = state?.sellActive !== false;
  const name = countryName(code);
  return h("section", { className: `cjms-card${active ? "" : " cjms-card--inactive"}`, "aria-label": name },
    h("header", { className: "cjms-card__head" },
      h("div", null,
        h("h5", null, `${name} (${code})`),
        h(CountryStatus, { state, t })
      ),
      h("button", {
        type: "button",
        className: "cjds-btn secondary cjms-btn-sm",
        disabled: busy || state?.status === "loading",
        onClick: () => onRefresh(code),
        title: t("h_cjms_refresh_country", "Refresh {country}", { country: name }),
      }, h(MdRefresh, { "aria-hidden": true }), " ", t("h_cjms_refresh_country", "Refresh {country}", { country: name }))
    ),

    state?.status === "error" ? h("div", { className: "cjds-alert", role: "alert" },
      h("p", null, state.error || t("h_cjms_err_quote", "CJ could not quote this country."))
    ) : null,

    state?.status === "success" ? (options.length
      ? h("div", { className: "cjms-methods", role: "radiogroup", "aria-label": t("h_cjms_methods_for", "Shipping methods for {country}", { country: name }) },
        ...options.map((o) => {
          const selected = o.carrier === state.selectedCarrier;
          return h("label", {
            key: o.carrier,
            className: `cjds-sim-carrier cjms-method${selected ? " cjds-sim-carrier--selected" : ""}${o.availableForAllVariants === false ? " cjms-method--partial" : ""}`,
          },
            h("input", { type: "radio", name: `cjms-carrier-${code}`, checked: selected, onChange: () => onSelectCarrier(code, o.carrier) }),
            h("span", { className: "cjds-sim-carrier__name" },
              o.carrier,
              ...(o.labels || []).map((label) => h("span", { key: label, className: `cjms-tag cjms-tag--${label}` }, t(...(LABEL_TEXT[label] || [label, label])))),
              selected ? h("span", { className: "cjms-tag cjms-tag--selected" }, t("h_cjms_label_selected", "Selected")) : null
            ),
            h("span", { className: "cjds-sim-carrier__days" }, o.deliveryDays ? `${o.deliveryDays} ${t("h_cj_days", "days")}` : "-"),
            h("span", { className: "cjds-sim-carrier__price" },
              money(o.price),
              Number(o.priceMin) > 0 && Number(o.priceMin) < Number(o.price)
                ? h("small", { className: "cjms-price-range" }, t("h_cjms_from_price", "from {price}", { price: money(o.priceMin) }))
                : null
            )
          );
        }))
      : h("p", { className: "cjds-muted cjms-no-method" }, t("h_cjms_no_method", "No shipping method available for this destination.")))
      : null,

    option ? h("div", { className: "cjms-card__pricing" },
      h("div", { className: "cjds-field" },
        h("label", { htmlFor: `cjms-price-${code}` }, t("h_cjms_country_price", "Selling price for {country}", { country: code })),
        h("input", {
          id: `cjms-price-${code}`,
          type: "number", min: 0, step: "0.01",
          value: state.sellingPriceOverride || "",
          placeholder: defaultSellingPrice > 0 ? String(Number(defaultSellingPrice).toFixed(2)) : t("h_cj_shipping_sale_price_ph", "e.g. 29.99"),
          onChange: (e) => onOverrideChange(code, e.target.value),
        }),
        h("span", { className: "cjds-muted" }, computed?.usesOverride
          ? t("h_cjms_price_override_note", "Saved for this country and used in its profit simulation. Checkout currently charges the product price ({price}).", { price: money(defaultSellingPrice) })
          : t("h_cjms_price_default_note", "Empty = product price."))
      ),
      priceMode === "profit" && computed?.requiredSellingPrice ? h("div", { className: "cjms-required" },
        h("span", null, t("h_cjms_required_price", "Price for {profit} profit here", { profit: money(desiredProfit) })),
        h("strong", null, money(computed.requiredSellingPrice)),
        h("button", {
          type: "button",
          className: "cjds-btn secondary cjms-btn-sm",
          onClick: () => onOverrideChange(code, String(computed.requiredSellingPrice)),
        }, t("h_cjms_use_price", "Use"))
      ) : null,
      pricing ? h("div", { className: `cjms-profit ${profit >= 0 ? "cjms-profit--ok" : "cjms-profit--loss"}` },
        h("span", null, t("h_cjms_profit", "Profit")),
        h("strong", null, money(profit)),
        h("span", { className: "cjms-profit__status" }, profit > 0 ? t("h_cj_shipping_status_profit", "Profit") : t("h_cj_shipping_status_loss", "Loss"))
      ) : h("p", { className: "cjds-muted" }, t("h_cj_shipping_enter_price", "Enter a target sale price or a desired profit to see the full breakdown.")),
      h("label", { className: "cjms-check cjms-active" },
        h("input", { type: "checkbox", checked: active, onChange: (e) => onToggleActive(code, e.target.checked) }),
        active ? t("h_cjms_sell_active", "Sell to {country}", { country: name }) : t("h_cjms_sell_inactive", "Not sold to {country}", { country: name })
      )
    ) : null
  );
};

module.exports = { CJShippingCountryCard };
